'use client'

import { useState, useEffect, type ReactNode } from 'react'
import Container from '@/components/structure/Container'
import AppRanking, { AppRankingTitle } from '@/components/content/app/AppRanking'
import Link from '@/components/ui/Link'
import { PREFETCH } from '@/lib/prefetch'
import Pill from '@/components/ui/Pill'
import { fetchDealCountsForApps } from '@/components/content/best/helpers'
import { createLogger } from '@/lib/logger'
import { isPopulatedObject, toSlug } from '@/lib/utils'
import { replaceTitleWordCasing } from '@/lib/formatting'
import type { App, Category, AppCategoryRecommendation } from '@/payload-types'

const logger = createLogger('BestsFeatured')

type BestsFeaturedProps = {
  apps: AppCategoryRecommendation[]
  otherApps?: AppCategoryRecommendation[]
  category: Category
  title: string
  text?: string
  subtitle?: string
  icon?: ReactNode
  buttonText?: string
  buttonLink?: string
  featured?: boolean
  // Server-provided deal count (e.g. on /best/:slug) — skips the client fetch.
  dealCount?: number
}

export default function BestsFeatured({
  apps,
  otherApps,
  category,
  title,
  text,
  subtitle,
  icon,
  buttonText,
  buttonLink,
  featured = false,
  dealCount: dealCountProp,
}: BestsFeaturedProps) {
  const year = new Date().getFullYear()
  const [dealCount, setDealCount] = useState(dealCountProp ?? 0)

  const useSingular = apps.length === 1
  const useDealCountSingular = dealCount === 1

  const nameLabel =
    (useSingular ? category?.nameLabelSingular : category?.nameLabelPlural) || 'Apps'

  const nameLabelLowercase = replaceTitleWordCasing(
    (useSingular
      ? category?.nameLabelSingular?.toLowerCase()
      : category?.nameLabelPlural?.toLowerCase()) || 'apps',
  )

  const appIds = apps
    .map((rec) => (isPopulatedObject<App>(rec.app) ? rec.app.id : rec.app))
    .filter(Boolean) as number[]
  const appIdsKey = appIds.join(',')

  useEffect(() => {
    if (dealCountProp !== undefined) return // server-provided on /best/:slug

    async function fetchAndSetDealCount() {
      try {
        const { dealCount } = await fetchDealCountsForApps(appIds)
        setDealCount(dealCount)
      } catch (error) {
        logger.error('Failed to fetch deal count for featured apps', {
          appIds,
          error: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
        })
      }
    }

    if (appIds.length > 0) fetchAndSetDealCount()
  }, [appIdsKey, dealCountProp])

  const sectionId = toSlug(title)
  const showNotableMentions = category && otherApps && otherApps.length > 0

  const appRankingTitleTitle = `Best ${nameLabel}`

  const appRankingTitleText = `${apps.length} app${useSingular ? '' : 's'}${dealCount > 0 ? ` and ${dealCount} deal${useDealCountSingular ? '' : 's'}` : ''}`

  return (
    <Container
      id={sectionId}
      className="relative mb-16 flex flex-col gap-6 md:mb-28 md:flex-row xl:gap-24"
    >
      <div
        data-section-app={true}
        data-section-id={sectionId}
        data-name={title}
        className="my-8 flex h-fit w-full flex-col gap-7 px-1.5 md:sticky md:top-28 md:mb-0 md:mt-14 md:w-1/2 lg:w-2/5"
      >
        {subtitle && (
          <div className="flex items-center gap-3 text-primary-500">
            {icon && icon}
            <span className="text-14 font-medium">{subtitle}</span>
          </div>
        )}
        <div className="flex flex-col gap-6">
          <span className="text-balance font-heading text-36 font-bold leading-tight text-white md:text-48">
            {title}
          </span>
          <div className="text-zinc-400">
            {text ||
              `Discover our handpicked selection of the best
            ${apps.length} ${nameLabelLowercase} of ${year}. See how they
            compare, what they do best, and why they made the list.`}
          </div>
        </div>

        {buttonText && buttonLink && (
          <Link href={buttonLink} prefetch={PREFETCH} className="hidden md:block">
            <Pill size="large" style="invert">
              {buttonText}
            </Pill>
          </Link>
        )}
      </div>

      {apps.length > 0 && (
        <div className="flex w-full flex-col gap-8 md:w-1/2 md:pt-8 lg:w-3/5">
          <AppRanking
            apps={apps}
            otherApps={!showNotableMentions ? otherApps : undefined}
            category={category}
            title={
              featured ? (
                <AppRankingTitle
                  title={appRankingTitleTitle}
                  icon="award"
                  text={appRankingTitleText}
                  link={`/best/${category.slug}`}
                  theme="dark"
                />
              ) : undefined
            }
            theme="dark"
            rank={featured ? 'ordered' : 'blank'}
          />

          {showNotableMentions && (
            <AppRanking
              apps={otherApps}
              otherApps={undefined}
              category={category}
              title={<AppRankingTitle title="Notable Mentions" theme="dark" />}
              rank="blank"
              theme="dark"
              isBest={true}
            />
          )}
        </div>
      )}
    </Container>
  )
}
